import { createSignal, Show } from 'solid-js'
import { formStore } from '/web/store/form'
import Footer from '/web/shared/aifans/Footer'
import { PageHero } from '/web/shared/aifans/PageHero'
import { A } from '@solidjs/router'
import { Title, Meta, Link } from '@solidjs/meta'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import { IconArrowRight } from '/web/shared/aifans/Icons'

export default function ContactUs() {
  const [name, setName] = createSignal('')
  const [email, setEmail] = createSignal('')
  const [issue, setIssue] = createSignal('')
  const [message, setMessage] = createSignal('')
  const [valid, setValid] = createSignal(true)
  const [checkEmail, setCheckEmail] = createSignal(false)
  const [loading, setLoading] = createSignal(false)
  const [success, setSuccess] = createSignal(false)

  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  const handleSubmit = async (e: Event) => {
    e.preventDefault()

    const validEmail = re.test(email())

    if (!name() || !validEmail) {
      setValid(false)
      return
    }

    setLoading(true)
    const result = await formStore.submitContactForm({
      name: name(),
      email: email(),
      issue: issue(),
      message: message(),
    })
    setLoading(false)

    if (result) {
      setSuccess(true)
      setName('')
      setEmail('')
      setIssue('')
      setMessage('')
      setValid(true)
      setCheckEmail(false)
    }
  }

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value)
    const validEmail = re.test(e.target.value)
    setCheckEmail(validEmail)
  }

  const resetForm = () => {
    setSuccess(false)
    setValid(true)
    setCheckEmail(false)
  }

  const inputClass =
    'w-full rounded-xl border border-[rgba(255,255,255,0.07)] bg-[rgba(255,255,255,0.04)] px-3.5 py-3 text-sm text-white outline-none transition-all duration-200 placeholder:text-[rgba(255,255,255,0.3)] focus:border-[rgba(0,229,255,0.4)] focus:bg-[rgba(0,229,255,0.04)] focus:shadow-[0_0_0_3px_rgba(0,229,255,0.12)]'

  const labelClass =
    'mb-1.5 block font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.55)]'

  return (
    <>
      <Title>CosplayFans - Contact | Reach Our Support & Services Team</Title>
      <Meta name="og:title" content="CosplayFans - Contact | Reach Our Support & Services Team" />
      <Meta
        name="description"
        content="Get in touch with the CosplayFans.ai team for support, questions, or inquiries. We're here to help with fan pages, live chat, ai generated images & more."
      />
      <Meta
        name="og:description"
        content="Get in touch with the CosplayFans.ai team for support, questions, or inquiries. We're here to help with fan pages, live chat, ai generated images & more."
      />
      <Link rel="canonical" href={getCanonicalUrl('/contact-us/')} />

      <main>
        {/* Hero */}
        <PageHero
          badge="We're Here to Help"
          title={<>CONTACT <span class="cf-grad-text">SUPPORT</span></>}
          subtitle="Got a question about your account, billing, or how something works? Drop us a line."
        />

        {/* Info cards */}
        <section class="cf-section !pt-0 !pb-4">
          <div class="mx-auto grid max-w-[900px] gap-4 sm:grid-cols-3 sm:gap-5">
            {/* Email Support */}
            <div
              class="rounded-2xl border border-[rgba(255,255,255,0.07)] px-6 py-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(138,43,226,0.4)] hover:shadow-[0_16px_36px_rgba(138,43,226,0.16)]"
              style={{ background: 'rgba(255,255,255,0.03)' }}

            >
              <div class="mb-3.5 text-[36px] leading-none">✉️</div>
              <h3 class="mb-2 font-bebas text-[22px] tracking-[0.8px] text-white leading-none">
                Email Support
              </h3>
              <p class="mb-4 text-[13px] leading-relaxed text-[var(--cf-muted)]">Get help with your account, billing, or technical issues.</p>
              <a
                href="mailto:support@cosplayfans.ai"
                class="inline-block rounded-full border border-[rgba(0,229,255,0.25)] bg-[rgba(0,229,255,0.08)] px-3.5 py-1.5 font-rajdhani text-[13px] font-bold text-[var(--cf-cyan)] transition-colors hover:bg-[rgba(0,229,255,0.18)]"
              >
                support@cosplayfans.ai
              </a>
            </div>

            {/* Response Time */}
            <div
              class="rounded-2xl border border-[rgba(255,255,255,0.07)] px-6 py-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(138,43,226,0.4)] hover:shadow-[0_16px_36px_rgba(138,43,226,0.16)]"
              style={{ background: 'rgba(255,255,255,0.03)' }}

            >
              <div class="mb-3.5 text-[36px] leading-none">🕐</div>
              <h3 class="mb-2 font-bebas text-[22px] tracking-[0.8px] text-white leading-none">
                Response Time
              </h3>
              <p class="mb-4 text-[13px] leading-relaxed text-[var(--cf-muted)]">We typically respond within 24 hours during business days.</p>
              <span class="inline-block rounded-full border border-[rgba(0,229,255,0.25)] bg-[rgba(0,229,255,0.08)] px-3.5 py-1.5 font-rajdhani text-[13px] font-bold text-[var(--cf-cyan)]">Mon – Fri, 9am – 6pm</span>
            </div>

            {/* FAQ */}
            <div
              class="rounded-2xl border border-[rgba(255,255,255,0.07)] px-6 py-7 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(138,43,226,0.4)] hover:shadow-[0_16px_36px_rgba(138,43,226,0.16)]"
              style={{ background: 'rgba(255,255,255,0.03)' }}

            >
              <div class="mb-3.5 text-[36px] leading-none">💬</div>
              <h3 class="mb-2 font-bebas text-[22px] tracking-[0.8px] text-white leading-none">
                FAQ
              </h3>
              <p class="mb-4 text-[13px] leading-relaxed text-[var(--cf-muted)]">Find quick answers to commonly asked questions.</p>
              <A
                href="/faq"
                class="inline-flex items-center gap-1 rounded-full border border-[rgba(0,229,255,0.25)] bg-[rgba(0,229,255,0.08)] px-3.5 py-1.5 font-rajdhani text-[13px] font-bold text-[var(--cf-cyan)] transition-colors hover:bg-[rgba(0,229,255,0.18)]"
              >
                Visit FAQ's →
              </A>
            </div>
          </div>
        </section>

        {/* Form card */}
        <section class="cf-section !pt-2 pb-16">
          <div
            class="mx-auto max-w-[900px] rounded-[22px] border border-[rgba(138,43,226,0.25)] p-6 sm:p-8 lg:p-12"
            style={{ background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(138,43,226,0.12), transparent 60%), rgba(255,255,255,0.02)' }}
          >
            <Show
              when={!success()}
              fallback={
                /* Success state */
                <div class="flex flex-col items-center py-8 text-center">
                  <div
                    class="mb-5 flex h-16 w-16 items-center justify-center rounded-full"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(45,198,83,0.2), rgba(0,229,255,0.1))',
                      border: '1px solid rgba(45,198,83,0.3)',
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="32"
                      height="32"
                      fill="none"
                      stroke="#2dc653"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h2 class="cf-h2 cf-grad-text mb-3">Message Sent!</h2>
                  <p class="mb-8 max-w-[40ch] font-nunito text-sm leading-relaxed text-[var(--cf-muted)]">
                    Thanks for reaching out. We've received your message and will get back to you
                    within 24 hours.
                  </p>
                  <div class="flex flex-col gap-3 sm:flex-row">
                    <button
                      onClick={resetForm}
                      class="rounded-xl px-6 py-3 font-rajdhani text-sm font-extrabold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
                        'box-shadow': '0 6px 24px rgba(138,43,226,0.35)',
                      }}
                    >
                      Send another
                    </button>
                    <A
                      href="/"
                      class="inline-flex items-center justify-center gap-2 rounded-xl border border-[rgba(255,255,255,0.12)] px-6 py-3 font-rajdhani text-sm font-extrabold uppercase tracking-wider text-white transition-all duration-200 hover:border-[var(--cf-cyan)] hover:text-[var(--cf-cyan)]"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="14"
                        height="14"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      Back to Home
                    </A>
                  </div>
                </div>
              }
            >
              {/* Form header */}
              <div class="mb-7 text-center">
                <h2 class="cf-h2 mb-2">Send Us a Message</h2>
                <p class="font-rajdhani text-[13px] text-[var(--cf-muted)]">
                  We read every message. Most replies go out within a day.
                </p>
              </div>

              {/* Form */}
              <form class="mx-auto grid max-w-[580px] gap-4" onSubmit={handleSubmit}>
                {/* Name + Email row */}
                <div class="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label class={labelClass}>
                      Your Name{' '}
                      <Show when={!valid() && !name()}>
                        <span class="normal-case tracking-normal text-[#ff6b6b]">(Required)</span>
                      </Show>
                    </label>
                    <input
                      type="text"
                      class={`${inputClass} ${!valid() && !name() ? '!border-[rgba(255,100,100,0.5)]' : ''}`}
                      placeholder="Your name"
                      value={name()}
                      onInput={(e) => setName(e.currentTarget.value)}
                    />
                  </div>
                  <div>
                    <label class={labelClass}>
                      Email{' '}
                      <Show when={!valid() && !checkEmail() && email()}>
                        <span class="normal-case tracking-normal text-[#ff6b6b]">
                          (Invalid Email)
                        </span>
                      </Show>
                      <Show when={!valid() && !email()}>
                        <span class="normal-case tracking-normal text-[#ff6b6b]">(Required)</span>
                      </Show>
                    </label>
                    <input
                      type="email"
                      class={`${inputClass} ${!valid() && (!checkEmail() || !email()) ? '!border-[rgba(255,100,100,0.5)]' : ''}`}
                      placeholder="you@example.com"
                      value={email()}
                      onInput={handleEmailChange}
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label class={labelClass}>Subject</label>
                  <input
                    type="text"
                    class={inputClass}
                    placeholder="What's this about?"
                    value={issue()}
                    onInput={(e) => setIssue(e.currentTarget.value)}
                  />
                </div>

                {/* Message */}
                <div>
                  <label class={labelClass}>Message</label>
                  <textarea
                    class={`${inputClass} min-h-[140px] resize-y`}
                    rows={5}
                    placeholder="Tell us how we can help..."
                    value={message()}
                    onInput={(e) => setMessage(e.currentTarget.value)}
                  />
                </div>

                {/* Submit */}
                <div>
                  <button
                    type="submit"
                    disabled={loading()}
                    class="flex w-full items-center justify-center gap-2 rounded-[14px] py-4 font-rajdhani text-[15px] font-extrabold uppercase tracking-wider text-white transition-all duration-200 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-50"
                    style={{
                      background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
                      'box-shadow': '0 6px 24px rgba(138,43,226,0.35)',
                    }}
                  >
                    {loading() ? (
                      <>
                        <svg
                          class="h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                        >
                          <circle cx="12" cy="12" r="10" opacity="0.25" />
                          <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <IconArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </Show>
          </div>
        </section>
      </main>
      <Footer />
    </>
  )
}
